'use client';

import { App } from '@capacitor/app';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { trackInsightsException } from '../../utils/telemetry/appinsights';
import { useRefreshHomepageStory } from './hooks/useStoryblok';

// The callers below are fire-and-forget, so without this a throw inside the refresh
// would vanish as an unhandled rejection — taking the invalidation after it with it.
const refreshAndReport = async (refresh: () => Promise<void>): Promise<void> => {
  try {
    await refresh();
  } catch (e: unknown) {
    trackInsightsException('CMS_REFRESH_FAILED', e);
  }
};

/*
 * Re-checks Storyblok for homepage content whenever the user comes back to it.
 * Mounted once, app-wide: the top alerts and the campaign banner render in the
 * header on every page, so an operational message has to reach a user sitting on
 * the menu or the cart, not just one looking at the home page.
 *
 * Renders nothing.
 */
export const CmsRefreshOnResume: React.FC = () => {
  const pathname = usePathname();
  const refreshHomepageStory = useRefreshHomepageStory();

  // Every route change, not only the ones landing on Home: the top alert renders on
  // all of them, and the bottom navigation links carry no onClick, so the pathname is
  // the only signal we get. Also runs on cold start, where the refresh is skipped
  // because the first fetch is already in flight.
  useEffect(() => {
    void refreshAndReport(refreshHomepageStory);
  }, [pathname, refreshHomepageStory]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      const onVisibilityChange = (): void => {
        if (document.visibilityState === 'visible') void refreshAndReport(refreshHomepageStory);
      };

      document.addEventListener('visibilitychange', onVisibilityChange);

      return () => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      };
    }

    // Backgrounding the native app never unmounts anything — the WebView keeps the
    // JS context — so resuming needs its own listener. Kept separate from the
    // appStateChange listener in useCapacitor, which is throttled to once an hour
    // and drives the OTA update / splash screen flow.
    let handle: PluginListenerHandle | undefined;
    let isUnmounted = false;

    void (async (): Promise<void> => {
      const registered = await App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) void refreshAndReport(refreshHomepageStory);
      });

      // The effect can be torn down before this resolves (React strict mode
      // double-invokes effects), which would otherwise leak the listener.
      if (isUnmounted) await registered.remove();
      else handle = registered;
    })();

    return () => {
      isUnmounted = true;
      void handle?.remove();
    };
  }, [refreshHomepageStory]);

  return null;
};
